/**
 * M2 P5: economy + missions. Pure, deterministic, engine-agnostic (no Three.js,
 * no Math.random). Per-station commodity prices vary deterministically so buy-low
 * / sell-high arbitrage exists; courier missions move goods between stations. The
 * client holds wallet/cargo/missions in its Sim object and mutates via these pure
 * trade ops (each returns new state or a no-op when invalid).
 */
import type { StationSpec } from './station.js';
import type { FactionId, FactionStanding } from './faction.js';
import { ALL_SHIP_TYPES, factionOf, type ShipSpec } from './ship.js';

export interface Commodity {
  id: string;
  name: string;
  basePrice: number; // credits per unit
}

export interface CargoItem {
  commodityId: string;
  qty: number;
}

export interface CargoHold {
  items: CargoItem[];
  capacity: number; // max total units across all items
}

export interface Wallet {
  credits: number;
}

export interface TradeResult {
  wallet: Wallet;
  cargo: CargoHold;
  ok: boolean; // false => no-op (invalid trade); wallet/cargo returned unchanged
}

export type MissionKind = 'courier';

export interface Mission {
  id: string;
  kind: MissionKind;
  fromStationId: string;
  toStationId: string;
  commodityId: string;
  qty: number;
  reward: number;
}

// Fixed catalogue (tunable, ship as-is).
export const COMMODITIES: Commodity[] = [
  { id: 'water-ice', name: 'Water Ice', basePrice: 20 },
  { id: 'ore', name: 'Raw Ore', basePrice: 45 },
  { id: 'electronics', name: 'Electronics', basePrice: 180 },
  { id: 'medicine', name: 'Medicine', basePrice: 320 },
  { id: 'luxuries', name: 'Luxuries', basePrice: 500 },
];

export const DEFAULT_CARGO_CAPACITY = 40; // units
export const STARTING_CREDITS = 1_000;
/** Price multiplier swing band around basePrice: 0.70x .. 1.30x. */
export const PRICE_MIN_FACTOR = 0.7;
export const PRICE_SPREAD = 0.6;

// --- pure functions (consecutive, each < 50 lines) ---

/** Deterministic integer hash from two seeds (mirrors npc.ts). */
const hash2 = (a: number, b: number): number =>
  (Math.imul(a >>> 0, 2654435761) ^ Math.imul(b >>> 0, 40503)) >>> 0;

/** Deterministic 32-bit FNV-1a-ish hash of a string (no Math.random). */
const strHash = (s: string): number => {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return h >>> 0;
};

export const commodityById = (id: string): Commodity | undefined =>
  COMMODITIES.find((c) => c.id === id);

/** Deterministic per-station unit price in [0.7x, 1.3x] of basePrice. */
export const priceAt = (commodity: Commodity, stationId: string): number => {
  const h = hash2(strHash(commodity.id), strHash(stationId));
  const factor = PRICE_MIN_FACTOR + ((h % 1000) / 1000) * PRICE_SPREAD;
  return Math.round(commodity.basePrice * factor);
};

/** Total units currently stored. */
export const cargoUsed = (cargo: CargoHold): number =>
  cargo.items.reduce((sum, it) => sum + it.qty, 0);

/** Units of one commodity currently held. */
export const heldQty = (cargo: CargoHold, commodityId: string): number =>
  cargo.items.find((it) => it.commodityId === commodityId)?.qty ?? 0;

/** Add qty (clamped to capacity), merging same id. Returns a NEW CargoHold. */
export const addCargo = (cargo: CargoHold, commodityId: string, qty: number): CargoHold => {
  if (qty <= 0) return cargo;
  const add = Math.min(qty, Math.max(0, cargo.capacity - cargoUsed(cargo)));
  if (add === 0) return cargo;
  const items = cargo.items.slice();
  const idx = items.findIndex((it) => it.commodityId === commodityId);
  if (idx >= 0) items[idx] = { commodityId, qty: (items[idx] as CargoItem).qty + add };
  else items.push({ commodityId, qty: add });
  return { ...cargo, items };
};

/** Remove up to qty units; drops the entry at 0. Returns a NEW CargoHold. */
export const removeCargo = (cargo: CargoHold, commodityId: string, qty: number): CargoHold => {
  if (qty <= 0) return cargo;
  const idx = cargo.items.findIndex((it) => it.commodityId === commodityId);
  if (idx < 0) return cargo;
  const have = (cargo.items[idx] as CargoItem).qty;
  const left = have - Math.min(qty, have);
  const items = cargo.items.slice();
  if (left <= 0) items.splice(idx, 1);
  else items[idx] = { commodityId, qty: left };
  return { ...cargo, items };
};

export const canAfford = (wallet: Wallet, cost: number): boolean => wallet.credits >= cost;
export const debit = (wallet: Wallet, amount: number): Wallet => ({ credits: wallet.credits - amount });
export const credit = (wallet: Wallet, amount: number): Wallet => ({ credits: wallet.credits + amount });

/** Buy qty at unitPrice. No-op (ok:false) on bad qty / no credits / no room. */
export const tradeBuy = (
  wallet: Wallet,
  cargo: CargoHold,
  commodityId: string,
  qty: number,
  unitPrice: number,
): TradeResult => {
  const cost = unitPrice * qty;
  const room = cargo.capacity - cargoUsed(cargo);
  if (qty <= 0 || !canAfford(wallet, cost) || qty > room) return { wallet, cargo, ok: false };
  return { wallet: debit(wallet, cost), cargo: addCargo(cargo, commodityId, qty), ok: true };
};

/** Sell qty at unitPrice. No-op (ok:false) on bad qty / not holding enough. */
export const tradeSell = (
  wallet: Wallet,
  cargo: CargoHold,
  commodityId: string,
  qty: number,
  unitPrice: number,
): TradeResult => {
  if (qty <= 0 || heldQty(cargo, commodityId) < qty) return { wallet, cargo, ok: false };
  return { wallet: credit(wallet, unitPrice * qty), cargo: removeCargo(cargo, commodityId, qty), ok: true };
};

/** Deterministic courier missions — one per station, routed elsewhere. */
export const generateMissions = (stations: readonly StationSpec[], seed: number): Mission[] => {
  const out: Mission[] = [];
  const n = stations.length;
  if (n < 2 || COMMODITIES.length === 0) return out;
  for (let i = 0; i < n; i++) {
    const from = stations[i] as StationSpec;
    const to = stations[(i + 1 + (hash2(seed, i) % (n - 1))) % n] as StationSpec;
    const commodity = COMMODITIES[hash2(seed, i * 2 + 1) % COMMODITIES.length] as Commodity;
    const qty = 3 + (hash2(seed, i * 2 + 7) % 6);
    const reward = Math.round(commodity.basePrice * qty * 1.5) + 100;
    out.push({ id: `m-${i}`, kind: 'courier', fromStationId: from.id, toStationId: to.id, commodityId: commodity.id, qty, reward });
  }
  return out;
};

/** Complete iff docked at the destination AND holding >= the required qty. */
export const missionComplete = (mission: Mission, atStationId: string, cargo: CargoHold): boolean =>
  atStationId === mission.toStationId && heldQty(cargo, mission.commodityId) >= mission.qty;

export const initialCargo = (capacity = DEFAULT_CARGO_CAPACITY): CargoHold => ({ items: [], capacity });
export const initialWallet = (credits = STARTING_CREDITS): Wallet => ({ credits });

// --- SHIP OWNERSHIP (save v5): hangar purchasing. Pure + deterministic, mirroring
// the tradeBuy/credit patterns above. Switching the active hull / hangar UI is OUT
// of scope here; this is just the wallet + ownership math the save layer persists. ---

/** Per-class acquisition base price (credits). Heavier classes cost dramatically more. */
const SHIP_CLASS_BASE_PRICE: Record<ShipSpec['className'], number> = {
  light: 45_000,
  medium: 140_000,
  heavy: 480_000,
};

/** Reference mass per class — price scales linearly with mass around this point. */
const SHIP_CLASS_REF_MASS: Record<ShipSpec['className'], number> = {
  light: 20_000,
  medium: 45_000,
  heavy: 140_000,
};

/** Faction acquisition premium (guild/independent = baseline; rarer hulls cost more). */
const FACTION_PRICE_PREMIUM: Record<FactionId, number> = {
  guild: 1.0,
  independent: 1.0,
  belt: 1.05,
  aresian: 1.1,
  outer: 1.15,
  concord: 1.25,
  jovian: 1.3,
  quiet: 2.0,
};

/** Deterministic acquisition price for a hull: class base × mass scale × faction premium. */
export const shipPrice = (spec: ShipSpec): number =>
  Math.round(
    SHIP_CLASS_BASE_PRICE[spec.className] *
      (spec.massKg / SHIP_CLASS_REF_MASS[spec.className]) *
      FACTION_PRICE_PREMIUM[factionOf(spec.id)],
  );

/** Acquisition price table keyed by ship id (every resolvable hull). */
export const SHIP_PRICES: Record<string, number> = Object.fromEntries(
  ALL_SHIP_TYPES.map((s) => [s.id, shipPrice(s)]),
);

export type BuyShipReason = 'ok' | 'already-owned' | 'insufficient-funds' | 'unknown-ship';

export interface BuyShipResult {
  wallet: Wallet;
  ownedShips: string[];
  ok: boolean; // false => no-op (wallet/ownedShips returned unchanged)
  reason: BuyShipReason;
}

/**
 * Buy a hull. No-op (ok:false, with a reason) when already owned, the id is
 * unknown, or there aren't enough credits. On success the wallet is debited and
 * the ship id appended to a NEW ownedShips array. Pure + deterministic.
 */
export const buyShip = (
  wallet: Wallet,
  shipId: string,
  ownedShips: readonly string[],
): BuyShipResult => {
  const owned = [...ownedShips];
  if (owned.includes(shipId)) return { wallet, ownedShips: owned, ok: false, reason: 'already-owned' };
  const price = SHIP_PRICES[shipId];
  if (price === undefined) return { wallet, ownedShips: owned, ok: false, reason: 'unknown-ship' };
  if (!canAfford(wallet, price)) return { wallet, ownedShips: owned, ok: false, reason: 'insufficient-funds' };
  return { wallet: debit(wallet, price), ownedShips: [...owned, shipId], ok: true, reason: 'ok' };
};

/** Light faction-standing helper for future purchase gating. Neutral default. */
export const factionStandingOf = (standing: FactionStanding, faction: FactionId): number =>
  standing[faction] ?? 0;

/**
 * Boot helper: ensure the active ship id is one the player owns. Returns the
 * candidate when owned, else the first owned hull (then the candidate as a last
 * resort if ownedShips is empty). Pure + deterministic.
 */
export const resolveOwnedShipId = (candidate: string, ownedShips: readonly string[]): string =>
  ownedShips.includes(candidate) ? candidate : (ownedShips[0] ?? candidate);
